/**
 * Shared plumbing for the two settings section builders.
 *
 * `feed-settings-sections.ts` and `social-settings-sections.ts` turn a legacy
 * record into the `SettingsSection[]` the redesigned screens take. Both need
 * the same four things — read a dot path off a record that is plain JSON off
 * the API, coerce legacy's 0/1/"0"/"1"/true zoo into the renderer's types,
 * count which tab a validation error belongs to, and work out which tabs hold
 * unsaved edits — so those live here rather than twice.
 *
 * NOTHING HERE EDITS. This module produces the read half — real tabs, real
 * values, real error and dirty marks. What a control reports is turned back
 * into stored writes by `settings-changes.ts`, which the hosts replay through
 * `FormUtilityMixin.requestChange`.
 */

import {
  type SettingsField,
  type SettingsOption,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"

/**
 * `app.config.constants` — the constant table the bootstrap renders out of
 * `code/classes/types.php`. Passed in rather than imported because it is
 * config that arrives at runtime, which is also how every legacy panel reads
 * it.
 */
export type Constants = Record<string, number>

/** A record off the API. Legacy JSON, so assume nothing is present. */
export type LegacyRecord = Record<string, any>

/* ─────────────────────────────────────────────────────────────── reading ── */

/** Walk a dot path: `readPath(input, "attrs.hashTags.hashPool")`. */
export function readPath(
  record: LegacyRecord | null | undefined,
  path: string
): unknown {
  let cursor: unknown = record

  for (const key of path.split(".")) {
    if (cursor === null || typeof cursor !== "object") return undefined
    cursor = (cursor as Record<string, unknown>)[key]
  }

  return cursor
}

/**
 * Truthiness for a value that may have been through the DOM.
 *
 * The API's own types are sound — the PDO driver casts, so an int arrives an
 * int, and the `parseInt` littered through the legacy panels is a survival of
 * a time when that was not true. What is NOT sound is a value the user has
 * touched: everything read back off a control is a string, so the same field
 * is `0` before an edit and `"0"` after, and `"0"` is truthy in JS.
 */
export function flag(value: unknown): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0

  if (typeof value === "string") {
    const parsed = parseInt(value, 10)

    return Number.isNaN(parsed) ? value !== "" : parsed !== 0
  }

  return false
}

/** A text field's value. `null` and `undefined` become the empty string. */
export function text(value: unknown): string {
  return value === null || value === undefined ? "" : String(value)
}

/**
 * A select's or segmented control's value.
 *
 * ALWAYS A STRING, AND NEVER `undefined` WHEN THE RECORD HOLDS A VALUE: Radix
 * treats `undefined` as "uncontrolled" and stops reflecting the prop, so a
 * numeric `0` off the record has to survive as `"0"`.
 */
export function choice(value: unknown): string | undefined {
  return value === null || value === undefined || value === ""
    ? undefined
    : String(value)
}

/** `[{value, label}]` from legacy's number-keyed option arrays. */
export function options(
  list: { value: number | string; label: string }[]
): SettingsOption[] {
  return list.map((option) => ({
    value: String(option.value),
    label: option.label,
  }))
}

/* ──────────────────────────────────────────────────────────────── errors ── */

/**
 * Every key a validation error names, at any depth.
 *
 * The API returns `{errors: {validation: {...}}}` keyed by the field that
 * failed, sometimes nested (`attrs: {prefixText: "..."}`) and sometimes a list
 * (`rules: [{search: "..."}]`). Legacy's `ErrorHelper.buttonHasError` searches
 * the same tree for a key in the tab's `fieldMappings`.
 *
 * TWO DELIBERATE DIFFERENCES from that search. It collects CONTAINER keys as
 * well as leaves, so an error on `rules` or `sched` is attributable to the tab
 * that owns the widget rather than only to whichever inner key failed; and it
 * walks every branch, where legacy `return`s inside its own `for` loop and so
 * only ever inspects the first. Both make the tab badge report more, never
 * less.
 */
export function validationKeys(app: LegacyRecord | null | undefined): Set<string> {
  const keys = new Set<string>()
  const errors = [
    ...(app?.errors?.api ?? []),
    ...(app?.errors?.local ?? []),
  ]

  const walk = (node: unknown) => {
    if (node === null || typeof node !== "object") return

    if (Array.isArray(node)) {
      node.forEach(walk)

      return
    }

    for (const [key, value] of Object.entries(node)) {
      keys.add(key)
      walk(value)
    }
  }

  for (const entry of errors) {
    walk(entry?.error?.errors?.validation)
  }

  return keys
}

/**
 * Section id -> how many of its fields failed validation, for the tab badge.
 *
 * `map` is the tab's legacy `fieldMappings` list: the validation keys that
 * belong to it. Sections with no failures are left out, which is what
 * `errorCounts` wants — a `0` would still be falsy, but an absent key says the
 * same thing without inviting the reader to wonder.
 */
export function sectionErrorCounts(
  keys: Set<string>,
  map: Record<string, string[]>
): Record<string, number> {
  const counts: Record<string, number> = {}

  for (const [section, fields] of Object.entries(map)) {
    const hits = fields.filter((field) => keys.has(field)).length

    if (hits) counts[section] = hits
  }

  return counts
}

/**
 * The validation tree from the last failed write, or null.
 *
 * It MIRRORS THE RECORD, which is the whole reason this works:
 * `Input::validate` builds `$errs['name']`, `$errs['attrs']['prefixText']`,
 * `$errs['rules'][$idx]['search']` — the same shape, and so the same dot
 * paths, that `SettingsField.name` already carries. Looking a field's error up
 * is `readPath` against this instead of against the record.
 */
export function validationTree(
  app: LegacyRecord | null | undefined
): LegacyRecord | null {
  const entries = [...(app?.errors?.api ?? []), ...(app?.errors?.local ?? [])]

  for (const entry of entries) {
    const tree = entry?.error?.errors?.validation

    if (tree && typeof tree === "object") return tree as LegacyRecord
  }

  return null
}

/** The message for one record-relative path, when the server gave a string. */
export function validationMessage(
  tree: LegacyRecord | null,
  path: string
): string | undefined {
  const found = readPath(tree, path)

  /* A container — `rules` when the failures are per row — is not this field's
     message. Those reach their own rows instead. */
  return typeof found === "string" ? found : undefined
}

/**
 * Hang each field's error off it: the server's, else the one the last edit was
 * refused with locally.
 *
 * ONE PASS OVER FINISHED SECTIONS rather than a parameter threaded through
 * every section function, so a new field is covered the moment it carries a
 * `name`.
 *
 * The server wins where both exist. A local refusal is about what was just
 * typed; a validation error came back from the write the reader is trying to
 * complete, and that is the one blocking them.
 */
/**
 * A HASHTAG in an affix, the way the server counts one.
 *
 * `MATCH_HASHES_REGEX` in `code/classes/types.php:1078` is
 * `/(^|\s)#([\p{L}\p{N}_]+)/siu`, and all four validators that reject a `#` in
 * a prefix or suffix use it — `Input::validate`, `postAccount`, `OutputRule`
 * and `postTwitter`. This is that regex, so the field cannot refuse something
 * the server would have taken, or pass something it will reject.
 *
 * `#` HAS TO BE ANCHORED TO A WORD, which is the whole reason this is not
 * `value.includes("#")`: the server accepts `C#` (no whitespace before the
 * `#`), a bare `#`, and `a # b` (no word character after it). A naive contains
 * check would put an error under all three.
 *
 * `s` and `i` are dropped: there is no `.` to widen and no case to fold that
 * `\p{L}` does not already cover.
 */
const AFFIX_HASHTAG = /(^|\s)#[\p{L}\p{N}_]+/u

/**
 * The error for an affix that carries a hashtag, or undefined.
 *
 * DERIVED FROM THE VALUE, not refused on the way in, so it appears as the `#`
 * is typed and clears as it is removed. Refusing the change instead — the way
 * `feedFieldError` refuses a duplicate tag — would stop the character reaching
 * the box at all, leaving the reader to work out why their keyboard had
 * stopped working.
 *
 * IT DOES NOT BLOCK SAVING, and must not start to. Save is gated on `dirty`
 * alone, and the server only applies this rule to an affix that CHANGED — so a
 * record that already has a `#` in it (stored before the rule, or set through
 * the API) shows the error but still saves every other edit on the screen.
 * Gating Save on field errors would strand those records.
 *
 * `fix` is the second sentence, because where to put hashtags instead depends
 * on the screen: an automation has a Hashtags tab, a destination does not.
 */
export function affixHashtagError(
  value: string | undefined,
  fix: string
): string | undefined {
  if (!value || !AFFIX_HASHTAG.test(value)) return undefined

  /* The server says "Prefix cannot contain #". This says the same thing and
     then what to do about it, which is the house rule for an error. The two
     never appear together: the client catches it first, off the same regex. */
  return `Remove the #. ${fix}`
}

/** The switch that turns the whole record on and off, by id per screen. */
const ACTIVE_IDS = ["active", "output-active"]

/**
 * The Active switch goes LAST in its section, wherever it was built.
 *
 * Emily's rule, and it needs enforcing rather than arranging: every builder
 * here pushes fields in the order legacy's panels declare them, and Active was
 * pushed somewhere in the middle of that order. It happened to come out last
 * on the networks with nothing after it — X, Tumblr — and buried on the ones
 * that add their own fields, so the same switch moved around depending on
 * which destination you opened.
 *
 * Doing it here rather than by moving the `push` calls means a builder can go
 * on declaring fields in legacy's order, and a field added at the end later
 * cannot quietly push Active up again.
 */
export function activeLast(fields: SettingsField[]): SettingsField[] {
  const index = fields.findIndex((field) => ACTIVE_IDS.includes(field.id))

  if (index === -1 || index === fields.length - 1) return fields

  const reordered = [...fields]
  const [active] = reordered.splice(index, 1)

  reordered.push(active)

  return reordered
}

/**
 * The error for an affix carrying a hashtag, TOGETHER WITH the way out of it.
 *
 * The two travel as one because the link is only ever the fix for this error.
 * Set separately, `helpLink` outlived the error it belonged to and rendered on
 * the field's ordinary help line, so every affix box on every destination read
 * "...before anything the automation adds. Need hashtags?" whether or not
 * there was a hashtag anywhere near it.
 */
export function affixHashtagFault(
  value: string | undefined,
  fix: string
): Pick<SettingsField, "error" | "helpLink"> {
  const error = affixHashtagError(value, fix)

  return error ? { error, helpLink: { label: "Need hashtags?" } } : {}
}

/**
 * A section's fields including any nested inside another.
 *
 * A `listField` is a full field that happens to DRAW inside its parent's box —
 * it keeps its own name and is stored exactly like a top-level one. So
 * anything walking a section to find what it writes has to walk into it too,
 * or that field silently stops being tracked the moment it is nested.
 */
export function flattenFields(fields: SettingsField[]): SettingsField[] {
  return fields.flatMap((field) => {
    const nested: SettingsField[] = [field]

    if (field.listField) nested.push(field.listField)

    /*
     * A paired checkbox is its own param on someone else's row, so it has to
     * be walkable or the two affix space flags fall out of the dirty diff and
     * the error mapping — invisibly, because the row still draws.
     *
     * `kind: "toggle"` is right whatever the widget: this describes the PARAM,
     * a boolean, and the checkbox is only how it is drawn.
     */
    if (field.pairedCheck?.name) {
      nested.push({
        id: field.pairedCheck.id,
        name: field.pairedCheck.name,
        label: field.pairedCheck.label,
        kind: "toggle",
        checked: field.pairedCheck.checked,
      })
    }

    return nested
  })
}

export function attachErrors(
  sections: SettingsSection[],
  options: {
    tree?: LegacyRecord | null
    prefix: string
    fieldErrors?: Record<string, string>
  }
): SettingsSection[] {
  const { tree = null, prefix, fieldErrors } = options

  const decorate = (field: SettingsField): SettingsField => {
    const path = field.name?.startsWith(`${prefix}.`)
      ? field.name.slice(prefix.length + 1)
      : field.name
    const error =
      (path ? validationMessage(tree, path) : undefined) ??
      (fieldErrors ? fieldErrors[field.id] : undefined)
    /* A nested field carries its own error, on itself, where it draws. */
    const listField = field.listField ? decorate(field.listField) : undefined

    if (!error && listField === field.listField) return field

    return {
      ...field,
      ...(error ? { error } : {}),
      ...(listField ? { listField } : {}),
    }
  }

  return sections.map((section) => ({
    ...section,
    fields: section.fields.map(decorate),
  }))
}

/* ───────────────────────────────────────────────────────────────── dirty ── */

/** Structural equality over the scalars, arrays and plain objects legacy stores. */
function same(a: unknown, b: unknown): boolean {
  if (a === b) return true

  // The record uses null and undefined interchangeably for "not set", and an
  // untouched field must not read as edited because the API omitted it.
  if (a === null || a === undefined) return b === null || b === undefined
  if (b === null || b === undefined) return false

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false
    }

    return a.every((item, i) => same(item, b[i]))
  }

  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a as object)
    const bKeys = Object.keys(b as object)

    if (aKeys.length !== bKeys.length) return false

    return aKeys.every((key) =>
      same(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
    )
  }

  /*
   * `1` and `"1"` are the same setting, not an edit. Not because the API is
   * inconsistent — it is not — but because a value that has been through a
   * control comes back a string, so re-picking the value a field already had
   * would otherwise light the tab's dirty dot for a change nobody made.
   */
  return String(a) === String(b)
}

/**
 * Which sections hold unsaved edits, for the tab's dirty dot.
 *
 * `paths` is section id -> the record paths that section writes, RELATIVE TO
 * THE RECORD (`attrs.prefixText`, not `input.attrs.prefixText`), so the caller
 * derives it from the `SettingsField.name`s it just built and the two cannot
 * drift.
 */
export function dirtySectionIds(
  paths: Record<string, string[]>,
  record: LegacyRecord | null | undefined,
  clean: LegacyRecord | null | undefined
): string[] {
  if (!record || !clean) return []

  return Object.entries(paths)
    .filter(([, fields]) =>
      fields.some((path) => !same(readPath(record, path), readPath(clean, path)))
    )
    .map(([section]) => section)
}

/**
 * Section id -> record-relative paths, read off the sections themselves.
 *
 * `prefix` is the `SettingsField.name` root the screen uses — "input" or
 * "social" — and is stripped, because the dirty check runs against the record
 * the container holds in state, not against `this.state`.
 *
 * `composite` covers the fields whose one `name` stands for several stored
 * values: the filter checkbox group is five booleans, the shortener select
 * writes two ids. Without it those fields would never read as dirty.
 */
export function sectionPaths(
  sections: SettingsSection[],
  prefix: string,
  composite: Record<string, string[]> = {}
): Record<string, string[]> {
  const paths: Record<string, string[]> = {}

  for (const section of sections) {
    const fields = new Set<string>()

    /* Nested fields included — see `flattenFields`. */
    for (const field of flattenFields(section.fields)) {
      if (!field.name) continue

      for (const path of composite[field.name] ?? [field.name]) {
        fields.add(
          path.startsWith(`${prefix}.`) ? path.slice(prefix.length + 1) : path
        )
      }
    }

    paths[section.id] = [...fields]
  }

  return paths
}

/* ─────────────────────────────────────────────────────────────── filters ── */

/**
 * The five places a filter can look. Stored as five independent booleans, not
 * a list, which is why `FILTER_SOURCES` is also the option list: an option's
 * value IS its field name.
 */
export const FILTER_SOURCES = [
  { value: "filtersTitle", label: "Title" },
  { value: "filtersContent", label: "Content" },
  { value: "filtersCategory", label: "Category" },
  { value: "filtersAuthor", label: "Author" },
  { value: "filtersLink", label: "Link" },
]

/**
 * The Filters tab, which a route and a source share exactly.
 *
 * ONE BUILDER FOR BOTH because legacy has one component for both:
 * `RouteFilterPanel` and `InputFilterPanel` each render the same `FilterPanel`
 * and differ only in the `stateName` they pass it. §6 lists the two tabs as
 * identical field for field, so a second copy here would exist only to drift.
 *
 * `prefix` is that `stateName` — "input" or "route".
 */
export function filterFields(
  record: LegacyRecord,
  prefix: string
): SettingsField[] {
  const active = FILTER_SOURCES.filter((source) => flag(record[source.value]))
  const terms = [record.andFilter, record.orFilter, record.notFilter].some(
    (term) => text(term) !== ""
  )

  /*
   * `FilterPanel.shouldShowWarning` — a filter half-configured does nothing at
   * all, and neither half looks wrong on its own. This is the state the
   * `warning` tone exists for: not an error, not permanent help.
   */
  const halfDone =
    terms && !active.length ? "sources" : !terms && active.length ? "terms" : null

  return [
    {
      id: "filters",
      name: `${prefix}.filters`,
      label: "Where should we look?",
      kind: "checkbox",
      options: FILTER_SOURCES,
      values: active.map((source) => source.value),
      warning:
        halfDone === "sources"
          ? "Nothing is filtered yet — choose at least one place to look."
          : undefined,
    },
    {
      id: "filter-behavior",
      name: `${prefix}.filterBehavior`,
      label: "Filter behavior",
      kind: "segmented",
      options: options([
        { value: 1, label: "Match whole words" },
        { value: 0, label: "Match sequence anywhere" },
      ]),
      value: choice(record.filterBehavior) ?? "0",
      warning:
        halfDone === "terms" ? "Nothing is filtered yet — add terms below." : undefined,
    },
    {
      id: "and-filter",
      name: `${prefix}.andFilter`,
      label: "Must contain all of these terms",
      kind: "input",
      value: text(record.andFilter),
      placeholder: "ai, startups",
      help: "Separate terms with a comma.",
    },
    {
      id: "or-filter",
      name: `${prefix}.orFilter`,
      label: "Must contain any of these terms",
      kind: "input",
      value: text(record.orFilter),
      placeholder: "ai, startups",
      help: "Separate terms with a comma.",
    },
    {
      id: "not-filter",
      name: `${prefix}.notFilter`,
      label: "Must not contain any of these terms",
      kind: "input",
      value: text(record.notFilter),
      placeholder: "sponsored, giveaway",
      help: "Separate terms with a comma.",
    },
  ]
}

/** Those five booleans, for the dirty check. */
export const FILTER_PATHS = FILTER_SOURCES.map((source) => source.value)

/* ──────────────────────────────────────────────────────── the scope note ── */

/**
 * Where each settings screen remembers that its scope note was dismissed.
 *
 * PER SCREEN, NOT ONE FLAG, because the four notes say different things and
 * dismissing one is not an answer about the others: the source note points
 * DOWN at its destinations, the route note points up and down, and the
 * destination note is about automated posts. Someone who has understood one
 * has not necessarily seen the rest.
 *
 * Plain alphanumeric keys on purpose. `PersistData` falls back to cookies when
 * `Storage` is missing, and finds them with `entries[i].match(key)` — a regex,
 * so a key carrying `.` or `:` would match more loosely than intended.
 */
export const SETTINGS_NOTE_KEY = {
  automation: "settingsNoteAutomation",
  source: "settingsNoteSource",
  destination: "settingsNoteDestination",
  route: "settingsNoteRoute",
} as const
